import cx from 'classnames';
import i18next, { t } from 'i18next';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { SelectInstance } from 'react-select';
import { setPathLocale } from '../../../common/localeShared';
import { commonRoutes } from '../../../gameConfig/commonRoutes';
import { resolveRoutePath } from '../../bundles/routes';
import { useBrowserPlatform } from '../../scripts/hooks/useBrowserPlatform';
import { useIsMobile } from '../../scripts/hooks/useIsMobile';
import { useKeyPress } from '../../scripts/hooks/useKeyPress';
import { EntityAsyncSelect, TSuggestionOption } from '../entityAsyncSelect';
import { Media } from '../presentational/media';
import { IRioSelectProps } from '../presentational/select/types';

const KEY_TO_FOCUS_SEARCH_BAR = 'k';

export const MainNavSearchBar: FC<any> = (props: any) => {
  const isMobile = useIsMobile();
  const platform = useBrowserPlatform();
  const navigate = useNavigate();
  const [isFocused, setIsFocused] = useState(false);
  const [search, setSearch] = useState('');
  const selectRef = useRef<SelectInstance | null>(null);

  const metaKey = platform === 'mac' ? 'Meta' : 'Control';

  // If enter is pressed and there is a search and no option is focused, go to advanced search
  useKeyPress('Enter', {
    onDown: () => {
      const container = selectRef.current?.menuListRef;

      if (container && !!search.trim().length) {
        const isAnyMenuOptionFocused = container.querySelector('.rio-select__option--is-focused');

        if (isAnyMenuOptionFocused) {
          return;
        }

        gotoAdvanced();
        selectRef.current?.blur();
        setSearch('');
      }
    },
    disabled: !isFocused
  });

  // If KEY_TO_FOCUS_SEARCH_BAR is pressed and meta key is pressed, focus the search bar
  useKeyPress(KEY_TO_FOCUS_SEARCH_BAR, {
    onDown: (event) => {
      const key = platform === 'mac' ? 'metaKey' : 'ctrlKey';

      if (event[key]) {
        selectRef.current?.focus();
      }
    },
    disabled: isMobile
  });

  const metaKeyPressed = useKeyPress(metaKey, { disabled: isMobile || !isFocused });

  const onSelect = (_: any, option: TSuggestionOption) => {
    const openInNew = metaKeyPressed;

    let label = '';

    switch (option.type) {
      case 'guild':
        label = `<${option.data.displayName}> (${option.data.region.short_name}-${option.data.realm.name})`;
        break;
      case 'team':
        label = `${option.data.name} (${option.data.region.short_name})`;
        break;
      case 'character':
        label = `${option.data.name} (${option.data.region.short_name}-${option.data.realm.name})`;
        break;
      case 'realm':
        label = `${option.data.region.short_name}-${option.data.realm.name}`;
        break;
      case 'boss':
        label = `${option.data.boss.name} in ${option.data.raid.name}`;
        break;
    }

    RIO_TRACK_EVENT('event', 'select_content', {
      content_type: `searched_${option.data.type}`,
      content_id: label
    });

    switch (option.type) {
      case 'guild': {
        const path = setPathLocale(
          resolveRoutePath(commonRoutes.GuildDetails, {
            region: option.data.region.slug,
            realm: option.data.realm.slug,
            guild: option.data.name
          }),
          i18next.language
        );
        if (openInNew) {
          window.open(path, '_blank');
        } else {
          navigate(path);
          setSearch('');
        }
        break;
      }
      case 'team': {
        const path = option.data.path ? setPathLocale(option.data.path, i18next.language) : '';
        if (openInNew) {
          window.open(path, '_blank');
        } else {
          navigate(path);
          setSearch('');
        }
        break;
      }
      case 'character': {
        const path = setPathLocale(
          resolveRoutePath(commonRoutes.CharacterDetails, {
            region: option.data.region.slug,
            realm: option.data.realm.slug,
            character: option.data.name
          }),
          i18next.language
        );
        if (openInNew) {
          window.open(path, '_blank');
        } else {
          navigate(path);
          setSearch('');
        }
        break;
      }
      case 'realm': {
        const path = setPathLocale(
          resolveRoutePath(commonRoutes.RealmHome, {
            region: option.data.region.slug,
            realm: option.data.realm.slug
          }),
          i18next.language
        );
        if (openInNew) {
          window.open(path, '_blank');
        } else {
          navigate(path);
          setSearch('');
        }
        break;
      }
      case 'boss': {
        const path = setPathLocale(
          resolveRoutePath(commonRoutes.BossRankings, {
            raid: option.data.raid.slug,
            region: option.data.region.slug,
            boss: option.data.boss.slug,
            difficulty: props.siteConfig.currentRaidDifficulty // FIXME: look at current location to determine value?
          }),
          i18next.language
        );
        if (openInNew) {
          window.open(path, '_blank');
        } else {
          navigate(path);
          setSearch('');
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const gotoAdvanced = () => {
    const path = setPathLocale('/search', i18next.language);
    if (search) {
      navigate(`${path}?type=character&name[0][contains]=${encodeURIComponent(search)}`);
    } else {
      navigate(path);
    }
  };

  const handleSearchChange: IRioSelectProps['onSearchChange'] = (newSearch, meta) => {
    if (meta?.action === 'input-change') {
      setSearch(newSearch);
    }
  };

  return (
    <div className="rio-topnav-search">
      <EntityAsyncSelect
        selectRef={selectRef}
        className={cx('rio-search-bar', {
          'rio-search-bar--blurred': !isFocused
        })}
        type="all"
        theme="light"
        onChange={onSelect}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        search={search}
        onSearchChange={handleSearchChange}
        suffix={
          <div className="d-inline-block" style={{ position: 'absolute', right: 0, top: 0, height: '100%' }}>
            <div className="slds-grid slds-grid--align-end slds-grid--vertical-align-center rio-full-height">
              {!isFocused && (
                <>
                  <Media greaterThan="sm" className="slds-col slds-p-right--xx-small">
                    <div className="keybind-cue">
                      {platform === 'mac' ? <i className="fa fa-command" style={{ fontSize: '0.6rem' }} /> : 'Ctrl'}
                    </div>
                  </Media>
                  <div className="slds-col slds-p-right--small">
                    <div className="keybind-cue">{KEY_TO_FOCUS_SEARCH_BAR.toUpperCase()}</div>
                  </div>
                </>
              )}

              <div className="slds-col slds-p-right--small">
                <i
                  className={cx('fa fa-sliders rio-pointer', !isFocused && 'text-raiderio')}
                  title={t('common:footer.advanced-search')}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    gotoAdvanced();
                  }}
                />
              </div>
            </div>
          </div>
        }
        components={{
          DropdownIndicator: Empty,
          DownChevron: Empty,
          IndicatorSeparator: Empty,
          LoadingIndicator: Empty
        }}
      />
    </div>
  );
};

const Empty = () => null;
